<template>
  <!-- b-share: after a share link (?code=), a small note that the code will be
       applied at checkout. Browser only (never in the SSR or the page cache),
       only where the shop takes orders, and it can be closed (the code is
       kept). Theme tokens only. -->
  <div v-if="shown" class="pc-note" role="status" aria-live="polite">
    <span class="pc-note__text">{{ text }}</span>
    <button type="button" class="pc-note__close" aria-label="Close this note" @click="dismiss">×</button>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {useBasketStore} from "./basketStore.js";
import {capturePendingCode, readPendingCode, noteText} from "./pendingCode.js";

const route = useRoute();
const basket = useBasketStore();
const code = ref("");
const hidden = ref(false);
const mounted = ref(false);
const HIDE_KEY = "dsk_code_note_hidden";

const text = computed(() => noteText(code.value));
const shown = computed(() => mounted.value && !!code.value && !hidden.value
  && basket.basket?.checkout?.available === true && !String(route.path).startsWith("/checkout"));

function refresh() {
  capturePendingCode();
  code.value = readPendingCode();
  try { hidden.value = sessionStorage.getItem(HIDE_KEY) === code.value; } catch (e) { hidden.value = false; }
  if (code.value && !basket.loaded) basket.load();
}
function dismiss() {
  hidden.value = true;
  try { sessionStorage.setItem(HIDE_KEY, code.value); } catch (e) { /* private mode: hidden for this page only */ }
}
onMounted(() => {
  mounted.value = true;
  refresh();
});
watch(() => route.fullPath, () => { if (mounted.value) refresh(); });
</script>

<style scoped>
.pc-note {
  position: fixed;
  left: 50%;
  bottom: max(1rem, env(safe-area-inset-bottom));
  transform: translateX(-50%);
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: min(32rem, calc(100vw - 2rem));
  padding: 0.5rem 0.5rem 0.5rem 0.875rem;
  border: 1px solid var(--t-line, rgba(0, 0, 0, 0.12));
  border-radius: var(--t-radius, 0.75rem);
  background: var(--t-surface, #fff);
  color: var(--t-ink, #1b1f1d);
  box-shadow: var(--t-shadow, 0 6px 20px rgba(0, 0, 0, 0.12));
  font-family: var(--t-font-body, inherit);
  font-size: 0.875rem;
  line-height: 1.4;
}
.pc-note__text { flex: 1; min-width: 0; }
.pc-note__close {
  flex: none;
  min-width: 2.75rem;
  min-height: 2.75rem;
  border: 0;
  border-radius: var(--t-radius-sm, 0.5rem);
  background: transparent;
  color: var(--t-muted, currentColor);
  font-size: 1.25rem;
  cursor: pointer;
}
.pc-note__close:focus-visible { outline: 2px solid var(--t-accent, currentColor); outline-offset: 2px; }
@media (prefers-reduced-motion: no-preference) {
  .pc-note { animation: pc-note-in 0.2s ease-out; }
  @keyframes pc-note-in { from { opacity: 0; transform: translate(-50%, 0.5rem); } to { opacity: 1; transform: translate(-50%, 0); } }
}
</style>
